// src/pages/QrKodPage.js
import React, { useState, useEffect } from "react";
import { API_BASE } from "../config";
import { QRCodeSVG } from "qrcode.react";

/* Dil desteği */
const t = {
  tr: {
    title: "QR Kod Oluştur",
    subtitle: "Kurumunuza özel QR kod oluşturun, indirin ve paylaşın",
    download: "QR Kodu İndir",
    share: "Paylaş",
    copy: "Linki Kopyala",
    copied: "Kopyalandı!",
    qrFor: "için QR Kod",
    yourLink: "Kurum Linkiniz",
    howToUse: "Nasıl Kullanılır?",
    steps: [
      "Müşterileriniz bu QR kodu okutarak kurum profilinize ulaşır",
      "Menü, hizmet listesi, randevu ve belge taleplerinizi görüntüleyebilir",
      "QR kodu restoranınızda, ofisinizde veya kartvizitinizde kullanabilirsiniz"
    ],
    loading: "Yükleniyor...",
    error: "Kurum bilgileri alınamadı"
  },
  en: {
    title: "Generate QR Code",
    subtitle: "Create a custom QR code for your institution, download and share",
    download: "Download QR Code",
    share: "Share",
    copy: "Copy Link",
    copied: "Copied!",
    qrFor: "QR Code for",
    yourLink: "Your Institution Link",
    howToUse: "How to Use?",
    steps: [
      "Your customers can access your institution profile by scanning this QR code",
      "They can view menu, services, appointments and document requests",
      "Use the QR code in your restaurant, office or business card"
    ],
    loading: "Loading...",
    error: "Could not fetch institution data"
  },
  de: {
    title: "QR-Code erstellen",
    subtitle: "Erstellen Sie einen individuellen QR-Code für Ihre Institution",
    download: "QR-Code herunterladen",
    share: "Teilen",
    copy: "Link kopieren",
    copied: "Kopiert!",
    qrFor: "QR-Code für",
    yourLink: "Ihr Institutionslink",
    howToUse: "Wie verwenden?",
    steps: [
      "Ihre Kunden können durch Scannen dieses QR-Codes auf Ihr Institutionsprofil zugreifen",
      "Sie können Menü, Dienstleistungen, Termine und Dokumentenanfragen anzeigen",
      "Verwenden Sie den QR-Code in Ihrem Restaurant, Büro oder auf Ihrer Visitenkarte"
    ],
    loading: "Wird geladen...",
    error: "Institutionsdaten konnten nicht abgerufen werden"
  }
};

export default function QrKodPage() {
  const [uiLang, setUiLang] = useState(
    localStorage.getItem("app_lang") || 
    localStorage.getItem("lang") || 
    "tr"
  );
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [qrValue, setQrValue] = useState("");
  
  const TT = t[uiLang] || t.tr;

  useEffect(() => {
    loadInstitutionData();
  }, []);

  const loadInstitutionData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      // Önce kullanıcının hangi kuruma ait olduğunu bul
      const profileRes = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!profileRes.ok) {
        setLoading(false);
        return;
      }

      const profileData = await profileRes.json();
      const kurumId = profileData?.data?.kurum_id;

      if (!kurumId) {
        setLoading(false);
        return;
      }

      // Kurum bilgilerini getir
      const kurumRes = await fetch(`${API_BASE}/kurumlar/${kurumId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (kurumRes.ok) {
        const kurumData = await kurumRes.json();
        setInstitution(kurumData.data);
        
        // QR kod için URL oluştur
        const baseUrl = window.location.origin;
        const qrUrl = `${baseUrl}/k/${kurumId}`;
        setQrValue(qrUrl);
      }
    } catch (error) {
      console.error("Kurum bilgileri yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = () => {
    const canvas = document.getElementById("qr-code-canvas");
    if (!canvas) return;

    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");

    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `qr-${institution?.slug || "kurum"}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const shareQR = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: institution?.firma_adi_tr || "Kurum QR Kod",
          text: `${TT.qrFor} ${institution?.firma_adi_tr}`,
          url: qrValue,
        });
      } catch (err) {
        console.log("Paylaşım iptal edildi");
      }
    } else {
      // Fallback: linki kopyala
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(qrValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{TT.loading}</p>
        </div>
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{TT.error}</h2>
          <p className="text-gray-600">Kurum bilgilerinize erişilemiyor.</p>
        </div>
      </div>
    );
  }

  const firmaAdi = institution[`firma_adi_${uiLang}`] || 
                   institution.firma_adi_tr || 
                   "Kurum";

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      {/* Dil seçici */}
      <div className="absolute top-4 right-4 flex gap-2">
        {["tr", "en", "de"].map((lang) => (
          <button
            key={lang}
            onClick={() => setUiLang(lang)}
            className={`w-10 h-10 rounded-full font-semibold transition ${
              uiLang === lang
                ? "bg-orange-600 text-white shadow-lg"
                : "bg-white text-gray-600 hover:bg-orange-100"
            }`}
          >
            {lang.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {TT.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {TT.subtitle}
          </p>
        </div>

        {/* Ana kart */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-8 p-8">
              {/* Sol taraf - QR Kod */}
              <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl">
                <div className="bg-white p-4 rounded-2xl shadow-lg mb-6">
                  <QRCodeSVG
                    id="qr-code-canvas"
                    value={qrValue}
                    size={240}
                    level="H"
                    includeMargin={true}
                    imageSettings={{
                      src: institution?.logo_url || "/logo192.png",
                      x: undefined,
                      y: undefined,
                      height: 48,
                      width: 48,
                      excavate: true,
                    }}
                  />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {firmaAdi}
                </h3>
                <p className="text-sm text-gray-500 text-center mb-6">
                  {TT.qrFor}
                </p>

                {/* Butonlar */}
                <div className="flex gap-4 w-full">
                  <button
                    onClick={downloadQR}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-4 rounded-xl transition shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {TT.download}
                  </button>
                  
                  <button
                    onClick={shareQR}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-xl transition shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    {TT.share}
                  </button>
                </div>
              </div>

              {/* Sağ taraf - Link ve bilgiler */}
              <div className="flex flex-col justify-center space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    {TT.yourLink}
                  </h3>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={qrValue}
                      readOnly
                      className="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition relative"
                    >
                      {copied ? (
                        <span className="text-green-600 font-semibold">
                          {TT.copied}
                        </span>
                      ) : (
                        TT.copy
                      )}
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">
                    {TT.howToUse}
                  </h3>
                  <ul className="space-y-3">
                    {TT.steps.map((step, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                          {index + 1}
                        </span>
                        <span className="text-gray-600">{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Kurum bilgileri */}
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-3">
                    {institution.logo_url ? (
                      <img
                        src={institution.logo_url}
                        alt={firmaAdi}
                        className="w-12 h-12 rounded-full object-cover border-2 border-orange-200"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center text-orange-600 font-bold text-xl">
                        {firmaAdi.charAt(0)}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-gray-800">
                        {firmaAdi}
                      </div>
                      {institution.sube_adi && (
                        <div className="text-sm text-gray-500">
                          {institution.sube_adi}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Önizleme kartı */}
          <div className="mt-8 bg-white rounded-2xl shadow-lg p-6 border border-orange-100">
            <h4 className="text-center text-gray-500 text-sm mb-4">
              Müşterilerinizin Göreceği Sayfa (Önizleme)
            </h4>
            <div className="border rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-orange-600 to-orange-500 p-4 text-white">
                <div className="flex items-center gap-3">
                  {institution.logo_url ? (
                    <img
                      src={institution.logo_url}
                      alt={firmaAdi}
                      className="w-10 h-10 rounded-full border-2 border-white"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      🏢
                    </div>
                  )}
                  <div>
                    <div className="font-bold">{firmaAdi}</div>
                    <div className="text-sm text-white/80">{institution.slogan_tr || "Hoş Geldiniz"}</div>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gray-50">
                <div className="flex gap-4 justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 mx-auto mb-2">
                      📋
                    </div>
                    <div className="text-xs">Menü</div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 mx-auto mb-2">
                      📅
                    </div>
                    <div className="text-xs">Randevu</div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 mx-auto mb-2">
                      📄
                    </div>
                    <div className="text-xs">Belge</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}